// Minimal RFC 4180 CSV parser.
//
// Hand-rolled rather than pulling in a dependency: we parse one small file
// shape (the locations import template), and the tricky parts are few —
// quoted fields containing commas or newlines, doubled quotes as an escape,
// CRLF line endings, and the UTF-8 BOM that Excel writes on "Save as CSV".

// Split raw text into rows of string cells. Empty trailing lines dropped.
export function parseCsv(text) {
  const src = String(text || '').replace(/^﻿/, ''); // strip Excel's BOM
  const rows = [];
  let row = [];
  let cell = '';
  let inQuotes = false;

  for (let i = 0; i < src.length; i++) {
    const ch = src[i];

    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') { cell += '"'; i++; }  // "" → literal quote
        else inQuotes = false;
      } else {
        cell += ch;
      }
      continue;
    }

    if (ch === '"') { inQuotes = true; continue; }
    if (ch === ',') { row.push(cell); cell = ''; continue; }
    if (ch === '\r') continue;                          // CRLF → LF
    if (ch === '\n') { row.push(cell); rows.push(row); row = []; cell = ''; continue; }
    cell += ch;
  }

  // Whatever is left over after the last newline
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }

  return rows.filter(r => r.some(c => String(c).trim() !== ''));
}

// Parse into objects keyed by header. Headers are lowercased and
// space/dash-normalized to snake_case so "Address Line 1", "address-line-1"
// and "address_line1" all land on the same field.
export function parseCsvRows(text) {
  const rows = parseCsv(text);
  if (!rows.length) return { headers: [], rows: [] };

  const headers = rows[0].map(h =>
    String(h).trim().toLowerCase()
      .replace(/[\s-]+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '')
  );

  const out = rows.slice(1).map((cells, i) => {
    const obj = { _line: i + 2 }; // 1-indexed, +1 for the header row
    headers.forEach((h, idx) => {
      if (!h) return;
      obj[h] = (cells[idx] ?? '').trim();
    });
    return obj;
  });

  return { headers, rows: out };
}

// Build a CSV string from column names + row objects (used for the
// downloadable template). Quotes anything containing a comma, quote,
// or newline.
export function toCsv(columns, rows) {
  const esc = (v) => {
    const s = v == null ? '' : String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [
    columns.join(','),
    ...rows.map(r => columns.map(c => esc(r[c])).join(',')),
  ].join('\n');
}
